/**
 * Auth contracts — login, refresh, logout, set-password.
 *
 * Two rules shape every schema in this file:
 *
 * 1. **The refresh token never appears in a request or response body.** It is
 *    carried by an httpOnly, SameSite=Lax, Secure cookie and is rotated on every
 *    use. That is why `refreshRequestSchema` and `logoutRequestSchema` are empty.
 * 2. **No password and no token value is ever returned or emailed.** Onboarding
 *    emails a link containing a single-use, 24h set-password token; the token
 *    value exists only in that email and in the user's browser, never in an API
 *    response, a log line, or an audit row.
 */
import { z } from 'zod';

import { emailSchema, isoDateTimeSchema, passwordSchema, uuidSchema } from '../common.js';
import { featureSchema, userRoleSchema } from '../enums.js';

/* -------------------------------------------------------------------------- */
/* Constants shared by the API and both frontends                              */
/* -------------------------------------------------------------------------- */

/** httpOnly refresh cookie for the tenant app (`apps/web`). */
export const REFRESH_COOKIE_NAME = 'wend_rt';
/** httpOnly refresh cookie for the internal admin panel (`apps/admin`). */
export const ADMIN_REFRESH_COOKIE_NAME = 'wend_admin_rt';

/** Set-password tokens live for exactly 24 hours and may be used once. */
export const SET_PASSWORD_TOKEN_TTL_HOURS = 24;

/* -------------------------------------------------------------------------- */
/* Session identity                                                            */
/* -------------------------------------------------------------------------- */

/** Everything the tenant app needs to render an authenticated shell. */
export const sessionUserSchema = z.object({
  id: uuidSchema,
  email: z.string().email(),
  name: z.string(),
  role: userRoleSchema,
  tenantId: uuidSchema,
  tenantName: z.string(),
  /** Enabled feature flags for the user's tenant. */
  features: z.array(featureSchema),
});
export type SessionUser = z.infer<typeof sessionUserSchema>;

/** Internal staff identity for the admin panel. Never tenant-scoped. */
export const adminSessionUserSchema = z.object({
  id: uuidSchema,
  email: z.string().email(),
  name: z.string(),
});
export type AdminSessionUser = z.infer<typeof adminSessionUserSchema>;

/* -------------------------------------------------------------------------- */
/* Login                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Login is email + password. There is no workspace picker and no tenant field:
 * `users.email` is globally unique, so the email resolves the tenant.
 *
 * `password` is only checked for presence here — the strength policy belongs to
 * the set-password path, not to login.
 */
export const loginRequestSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Password is required').max(512),
});
export type LoginRequest = z.infer<typeof loginRequestSchema>;

/** Access token + identity. The refresh token is set as an httpOnly cookie. */
export const loginResponseSchema = z.object({
  accessToken: z.string(),
  tokenType: z.literal('Bearer'),
  /** Access-token lifetime in seconds. */
  expiresIn: z.number().int().positive(),
  user: sessionUserSchema,
});
export type LoginResponse = z.infer<typeof loginResponseSchema>;

export const adminLoginRequestSchema = loginRequestSchema;
export type AdminLoginRequest = z.infer<typeof adminLoginRequestSchema>;

export const adminLoginResponseSchema = z.object({
  accessToken: z.string(),
  tokenType: z.literal('Bearer'),
  expiresIn: z.number().int().positive(),
  adminUser: adminSessionUserSchema,
});
export type AdminLoginResponse = z.infer<typeof adminLoginResponseSchema>;

/* -------------------------------------------------------------------------- */
/* Refresh + logout                                                            */
/* -------------------------------------------------------------------------- */

/**
 * Refresh takes no body: the token travels in the httpOnly cookie. The server
 * rotates it — the presented token is marked used and a new one is issued in a
 * fresh cookie. Presenting an already-used token revokes the whole family.
 */
export const refreshRequestSchema = z.object({}).strict();
export type RefreshRequest = z.infer<typeof refreshRequestSchema>;

export const refreshResponseSchema = loginResponseSchema;
export type RefreshResponse = z.infer<typeof refreshResponseSchema>;

export const adminRefreshResponseSchema = adminLoginResponseSchema;
export type AdminRefreshResponse = z.infer<typeof adminRefreshResponseSchema>;

/** Logout also takes no body; it revokes the cookie's token and clears it. */
export const logoutRequestSchema = z.object({}).strict();
export type LogoutRequest = z.infer<typeof logoutRequestSchema>;

export const logoutResponseSchema = z.object({ success: z.literal(true) });
export type LogoutResponse = z.infer<typeof logoutResponseSchema>;

/* -------------------------------------------------------------------------- */
/* Current session                                                             */
/* -------------------------------------------------------------------------- */

export const meResponseSchema = z.object({ user: sessionUserSchema });
export type MeResponse = z.infer<typeof meResponseSchema>;

export const adminMeResponseSchema = z.object({ adminUser: adminSessionUserSchema });
export type AdminMeResponse = z.infer<typeof adminMeResponseSchema>;

/* -------------------------------------------------------------------------- */
/* Set password (onboarding)                                                   */
/* -------------------------------------------------------------------------- */

/**
 * The opaque token from the set-password link. Only its SHA-256 hash is stored,
 * so a database read cannot reconstruct a usable link.
 */
export const setPasswordTokenSchema = z.string().trim().min(20).max(512);

/** Cheap pre-flight so the set-password screen can say "this link has expired". */
export const verifySetPasswordTokenQuerySchema = z.object({
  token: setPasswordTokenSchema,
});
export type VerifySetPasswordTokenQuery = z.infer<typeof verifySetPasswordTokenQuerySchema>;

export const verifySetPasswordTokenResponseSchema = z.object({
  valid: z.boolean(),
  /** Present only when valid, so the form can show whose account is being set up. */
  email: z.string().email().optional(),
  expiresAt: isoDateTimeSchema.optional(),
});
export type VerifySetPasswordTokenResponse = z.infer<typeof verifySetPasswordTokenResponseSchema>;

/**
 * Consuming the token sets the password (argon2id) and burns the token in the
 * same transaction. A token can never be redeemed twice.
 */
export const setPasswordRequestSchema = z.object({
  token: setPasswordTokenSchema,
  password: passwordSchema,
});
export type SetPasswordRequest = z.infer<typeof setPasswordRequestSchema>;

/** Deliberately returns no session: the user then logs in normally. */
export const setPasswordResponseSchema = z.object({
  success: z.literal(true),
  /** So the login form can be pre-filled. */
  email: z.string().email(),
});
export type SetPasswordResponse = z.infer<typeof setPasswordResponseSchema>;
